import { TableColumnCell } from './renders/renderCellByType';
import { TableColumnHeader } from './renders/renderHeaderByType';

/**
 * Convert size with unit to number
 * @param {string} value - The size with unit
 * @returns {number} The size in number
 */
const convertSizeWithUnitToNumber = (value) => {
  if (typeof value === 'number') return value;
  const trimmed = String(value).trim();
  const match = trimmed.match(/^(-?\d+(\.\d+)?)/);

  if (value == null || !trimmed || !match) return 100;

  return Number.parseFloat(match[1]);
};

/**
 * Parse a column width and return value and unit
 * @param {string | number | null | undefined} width - The width value
 * @returns {{ value: number, unit: 'px' | '%' }} Parsed width with unit
 */
export const parseColumnWidth = (width) => {
  // TODO : This function can be simplified.
  if (typeof width === 'number') {
    return { value: width, unit: 'px' };
  }

  if (width == null) {
    return { value: 100, unit: 'px' };
  }

  const trimmed = String(width).trim();
  const match = trimmed.match(/^(-?\d+(\.\d+)?)(px|%)?$/i);

  if (!match) {
    return { value: 100, unit: 'px' };
  }

  const value = Number.parseFloat(match[1]);
  const unit = match[3]?.toLowerCase() === '%' ? '%' : 'px';

  return { value, unit };
};

/**
 * Convert column widths from % to px based on table wrapper width
 * @param {TableColumnsReactTable[]} columns - The table columns
 * @param {number} tableWrapperWidth - The width of the table wrapper in pixels
 * @returns {TableColumnsReactTable[]} Columns with converted widths in px
 */
export const convertColumnsWidthToPx = (columns, tableWrapperWidth) => {
  if (!columns?.length || tableWrapperWidth <= 0) {
    return columns;
  }

  // Parse all column widths
  const parsedWidths = columns.map((column) => {
    const originalWidth = column.width || column.size || 100;
    return {
      column,
      parsed: parseColumnWidth(originalWidth),
    };
  });

  // Calculate sum of px widths
  const sumPx = parsedWidths.reduce((acc, { parsed }) => {
    if (parsed.unit === 'px') {
      return acc + parsed.value;
    }
    return acc;
  }, 0);

  // Calculate remaining space
  const remainingSpace = Math.max(0, tableWrapperWidth - sumPx);

  // Calculate sum of percentages
  const sumPercent = parsedWidths.reduce((acc, { parsed }) => {
    if (parsed.unit === '%') {
      return acc + parsed.value;
    }
    return acc;
  }, 0);

  // Convert columns with updated widths
  return columns.map((column, index) => {
    const { parsed } = parsedWidths[index];
    let newSize;

    if (parsed.unit === 'px') {
      // Keep px values as is
      newSize = parsed.value;
    } else if (parsed.unit === '%' && sumPercent > 0) {
      // Convert % to px proportionally
      newSize = remainingSpace ? (remainingSpace * parsed.value) / sumPercent : 100;
    } else {
      // Fallback to original size
      newSize = parsed.value;
    }

    return {
      ...column,
      size: newSize,
      minSize: newSize / 2,
    };
  });
};

/**
 * Create a table column from an API column
 * @param {ApiManagerTableColumns} column - The column
 * @returns {TableColumnsReactTable} The table column
 */
export const createTableColumn = (column) => {
  const size = convertSizeWithUnitToNumber(column.width);
  const headers = column.headers?.length
    ? column.headers.map((h) => ({
      ...h,
      align: /** @type {'left' | 'center' | 'right'} */ (
        h.align ?? column.align ?? 'left'
      ),
    }))
    : [{
      lib: column.lib ?? '',
      width: column.width ?? 'auto',
      colspan: 1,
      align: /** @type {'left' | 'center' | 'right'} */ (column.align ?? 'left'),
      backgroundColor: null,
      textDecoration: { strikeThrough: false, underline: false },
    }];
  /**
   * @type {TableColumnsReactTable}
   */
  const baseColumn = {
    ...column,
    headers,
    accessorFn: (
      /** @type {TableRowFlattened} */ row,
    ) => /** @type {TableCellValueObjectCustom | undefined} */ (row.cells?.[column.id]),
    size,
    minSize: size,
    enableSorting: column.type !== 'static-checkbox',
    enableResizing: column.enableResizing ?? true,
    header: TableColumnHeader,
    cell: TableColumnCell,
    footer: (props) => props.column.id,
  };

  return baseColumn;
};

/**
 * Generate table columns from API columns
 * @param {ApiManagerTableColumns[]} apiColumns - The API columns
 * @param {number} [tableWrapperWidth] - The width of the table wrapper in pixels (optional)
 * @returns {TableColumnsReactTable[]} The table columns
 */
export function generateTableColumns(apiColumns, tableWrapperWidth = 0) {
  if (!apiColumns) return [];

  const tableColumnsGenerated = apiColumns
    .map((apiColumn) => createTableColumn(apiColumn));

  // Convert % widths to px if tableWrapperWidth is available
  if (tableWrapperWidth > 0) {
    return convertColumnsWidthToPx(tableColumnsGenerated, tableWrapperWidth);
  }

  return tableColumnsGenerated;
}

/**
 *
 * @param {Array<ApiManagerTableCellValueObject>} cellsArray
 * @returns {Record<string, ApiManagerTableCellValueObject>}
 */
export function convertCellsArrayToObject(cellsArray) {
  if (!Array.isArray(cellsArray)) return {};

  return cellsArray.reduce((
    /** @type {Record<string, ApiManagerTableCellValueObject>} */ acc,
    /** @type {ApiManagerTableCellValueObject} */ cell,
  ) => {
    if (cell && cell.columnId) {
      acc[cell.columnId] = {
        ...cell,
      };
    }
    return acc;
  }, {});
}

/**
 * Body cell colspan: checkbox column never merges; otherwise use API `colspan` (default 1, floored to integer, min 1).
 * @param {TableCellValueObjectCustom | undefined} cellValue
 * @param {TableColumnsReactTable} columnDef
 * @returns {number}
 */
export function getEffectiveBodyColspan(cellValue, columnDef) {
  if (columnDef?.type === 'static-checkbox') {
    return 1;
  }
  const raw = cellValue?.colspan;
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.max(1, Math.floor(n));
}

/**
 * Transform raw data to table data
 * @param {ApiManagerTableGroups[]} groups - The groups
 * @param {ApiManagerTableColumns[]} columns - The API columns
 * @returns {TableGroupsCustom[]} The table data
 */
export function transformTableGroupData(groups, columns) {
  if (!groups || !columns) return [];

  const tableData = groups.reduce((
    /** @type {TableGroupsCustom[]} */ acc,
    /** @type {ApiManagerTableGroups} */ group,
  ) => {
    const groupData = group.data.map((row) => {
      const convertedCells = convertCellsArrayToObject(row.cells);
      const transformedRow = columns.reduce(
        (
          /** @type {Record<string, TableCellValueObjectCustom>} */ rowAcc,
          columnConfig,
        ) => {
          const cellValues = convertedCells[columnConfig.id];
          return /** @type {Record<string, TableCellValueObjectCustom>} */ ({
            ...rowAcc,
            [columnConfig.id]: {
              id: row.id,
              groupId: group.id,
              type: columnConfig.type,
              ...cellValues,
            },
          });
        },
        /** @type {Record<string, TableCellValueObjectCustom>} */ ({}),
      );

      return /** @type {TableRowCustom} */ ({
        ...row,
        cells: transformedRow,
      });
    });
    return [...acc, { ...group, data: groupData }];
  }, []);

  return tableData;
}

/**
 * Handle select group rows
 * @param {TableGroupsCustom} group - The group
 * @param {Record<string, Record<string, boolean>>} selectedRows - The selected rows
 * @returns {boolean} - True if all rows are selected, false otherwise
 */
export const areAllGroupRowsSelected = (group, selectedRows) => {
  const groupData = group.data.filter((row) => row.editable);
  return Object.keys(selectedRows[group.id] || {}).length === groupData.length && groupData.length > 0;
};

/**
 * Get editable rows from a group
 * @param {TableGroupsCustom} group - The group
 * @returns {TableRowCustom[]} The editable rows
 */
export const getEditableRows = (group) => /** @type {TableRowCustom[]} */ (group.data.filter((row) => row.editable));

/**
 * Ordered editable rows across groups for the current page (display order).
 * @param {TableGroupsCustom[]} tableGroups
 * @returns {Array<{ groupId: string, id: string }>}
 */
export const getEditableRowsOrdered = (tableGroups) => {
  if (!tableGroups?.length) return [];
  /** @type {Array<{ groupId: string, id: string }>} */
  const ordered = [];
  tableGroups.forEach((group) => {
    group.data.forEach((row) => {
      if (row.editable) {
        ordered.push({ groupId: group.id, id: row.id });
      }
    });
  });
  return ordered;
};

/**
 * Indices of anchor and target in ordered editable rows (same composite key match).
 * @param {Array<{ groupId: string, id: string }>} orderedEditableRows
 * @param {{ groupId: string, id: string }} anchor
 * @param {{ groupId: string, id: string }} target
 * @returns {[number, number] | null} Null if anchor or target not found
 */
const findAnchorTargetIndices = (orderedEditableRows, anchor, target) => {
  /**
   * @param {{ groupId: string, id: string }} ref
   * @returns {number}
   */
  const indexOf = (ref) => orderedEditableRows.findIndex(
    (r) => r.groupId === ref.groupId && r.id === ref.id,
  );
  const iAnchor = indexOf(anchor);
  const iTarget = indexOf(target);
  if (iAnchor === -1 || iTarget === -1) return null;
  return [iAnchor, iTarget];
};

/**
 * Inclusive range between anchor and target in display order (bidirectional).
 * Empty if anchor/target missing from list, or same row (caller should use simple toggle).
 * @param {Array<{ groupId: string, id: string }>} orderedEditableRows
 * @param {{ groupId: string, id: string }} anchor
 * @param {{ groupId: string, id: string }} target
 * @returns {Array<{ groupId: string, id: string }>}
 */
export const computeSelectionRange = (orderedEditableRows, anchor, target) => {
  const indices = findAnchorTargetIndices(orderedEditableRows, anchor, target);
  if (!indices) return [];
  const [iAnchor, iTarget] = indices;
  if (iAnchor === iTarget) return [];
  const start = Math.min(iAnchor, iTarget);
  const end = Math.max(iAnchor, iTarget);
  return orderedEditableRows.slice(start, end + 1);
};

/**
 * Calculate the total count of selected rows
 * @param {Record<string, Record<string, boolean>>} selectedRows - The selected rows object
 * @param {string} [groupId] - Optional group id to count only specific group rows
 * @returns {number} Total count of selected rows
 */
export const calculateSelectedRowsCount = (selectedRows, groupId = null) => {
  if (!selectedRows || typeof selectedRows !== 'object') {
    return 0;
  }

  // If groupId is specified, count only that group
  if (groupId !== null) {
    const groupRows = selectedRows[groupId] || {};
    if (!groupRows || typeof groupRows !== 'object') {
      return 0;
    }
    return Object.values(groupRows).filter(Boolean).length;
  }

  // Count all groups
  return Object.keys(selectedRows).reduce((acc, currentGroupId) => {
    const groupRows = selectedRows[currentGroupId];
    if (!groupRows || typeof groupRows !== 'object') {
      return acc;
    }
    return acc + Object.values(groupRows).filter(Boolean).length;
  }, 0);
};

/**
 * Tri-state selection for editable rows in one group (checkbox UI: none / indeterminate / all).
 * @typedef {'none'|'some'|'all'} EditableRowsSelectionState
 */

/**
 * Selection state for editable rows in a single table group.
 * @param {TableGroupsCustom} group - The group
 * @param {Record<string, Record<string, boolean>>} selectedRows - Selected rows state
 * @returns {EditableRowsSelectionState}
 */
export const getGroupRowsSelectionState = (group, selectedRows) => {
  const editableRows = getEditableRows(group);
  const editableCount = editableRows.length;
  if (editableCount === 0) {
    return 'none';
  }
  const selectedCount = calculateSelectedRowsCount(selectedRows, group.id);
  if (selectedCount === 0) {
    return 'none';
  }
  if (selectedCount === editableCount) {
    return 'all';
  }
  return 'some';
};

/**
 * Total count of editable rows across all groups.
 * @param {TableGroupsCustom[]} tableGroups - Table groups
 * @returns {number}
 */
export const countAllEditableRows = (tableGroups) => {
  if (!tableGroups?.length) {
    return 0;
  }
  return tableGroups.reduce(
    (acc, g) => acc + getEditableRows(g).length,
    0,
  );
};

/**
 * Selection state for all editable rows in the table (header "select all" checkbox).
 * @param {TableGroupsCustom[]} tableGroups - Table groups
 * @param {Record<string, Record<string, boolean>>} selectedRows - Selected rows state
 * @returns {EditableRowsSelectionState}
 */
export const getAllEditableRowsSelectionState = (tableGroups, selectedRows) => {
  const totalEditable = countAllEditableRows(tableGroups);
  if (totalEditable === 0) {
    return 'none';
  }
  const totalSelected = calculateSelectedRowsCount(selectedRows);
  if (totalSelected === 0) {
    return 'none';
  }
  if (totalSelected === totalEditable) {
    return 'all';
  }
  return 'some';
};

/**
 * True when the group checkbox should show the indeterminate (partial) state.
 * @param {TableGroupsCustom} group - The group
 * @param {Record<string, Record<string, boolean>>} selectedRows - Selected rows state
 * @returns {boolean}
 */
export const isGroupRowsSelectionIndeterminate = (group, selectedRows) => (
  getGroupRowsSelectionState(group, selectedRows) === 'some'
);

/**
 * True when the header "select all" checkbox should show the indeterminate (partial) state.
 * @param {TableGroupsCustom[]} tableGroups - Table groups
 * @param {Record<string, Record<string, boolean>>} selectedRows - Selected rows state
 * @returns {boolean}
 */
export const isAllRowsSelectionIndeterminate = (tableGroups, selectedRows) => (
  getAllEditableRowsSelectionState(tableGroups, selectedRows) === 'some'
);

/**
 * True if at least one visible header cell has a non-empty column-level `statValue` (API `columns[].statValue`).
 * @param {import('@tanstack/react-table').Table<any> | null | undefined} table - TanStack table instance
 * @returns {boolean}
 */
export const tableHasVisibleColumnStatValues = (table) => {
  if (!table) return false;
  return table.getHeaderGroups().some((headerGroup) => headerGroup.headers.some((header) => {
    const { statValue: sv } = /** @type {{ statValue?: string | null }} */ (header.column.columnDef);
    return sv != null && String(sv).trim() !== '';
  }));
};

// Utils
// These are the important styles to make sticky column pinning work!
// Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.
/**
 * Get common pinning styles
 * @param {import('@tanstack/react-table').Column<any>} column - The column
 * @returns {React.CSSProperties}
 */
export const getCommonPinningStyles = (column) => {
  const isPinned = column.getIsPinned();
  // const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize() ?? 100,
    maxWidth: column.getSize() ?? 100,
    zIndex: isPinned ? 1 : 0,
  };
};

// RESELECT ROWS AFTER BATCH PROCESSING
// Some batch processing actions may change groups, so we need to reselect the rows after the batch processing is done.
/**
 * Build selected rows state from row ids and current table groups.
 * Only rows present in tableGroups are selected; ids not found are ignored.
 * @param {string[]} ids - Row ids to select
 * @param {TableGroupsCustom[]} tableGroups - Current table groups
 * @returns {Record<string, Record<string, boolean>>} SelectedRowsState
 */
export const buildSelectedRowsFromIds = (ids, tableGroups) => {
  if (!ids?.length || !tableGroups?.length) return {};
  const idSet = new Set(ids);
  /** @type {Record<string, Record<string, boolean>>} */
  const newState = {};
  tableGroups.forEach((group) => {
    group.data.forEach((row) => {
      if (idSet.has(row.id)) {
        newState[group.id] = newState[group.id] || {};
        newState[group.id][row.id] = true;
      }
    });
  });
  return newState;
};

/**
 * True if at least one (groupId, rowId) in selectedRows does not exist in tableGroups.
 * @param {Record<string, Record<string, boolean>>} selectedRows - Current selection state
 * @param {TableGroupsCustom[]} tableGroups - Current table groups
 * @returns {boolean}
 */
export const selectionNeedsRehydrate = (selectedRows, tableGroups) => {
  if (!selectedRows || typeof selectedRows !== 'object' || !tableGroups?.length) return false;
  const groupById = new Map(tableGroups.map((g) => [g.id, g]));
  return Object.keys(selectedRows).some((groupId) => {
    const group = groupById.get(groupId);
    const rowIds = selectedRows[groupId] && typeof selectedRows[groupId] === 'object'
      ? Object.keys(selectedRows[groupId]).filter((id) => selectedRows[groupId][id])
      : [];
    return rowIds.some((rowId) => !group?.data?.some((row) => row.id === rowId));
  });
};
